import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';

import { MatSnackBar } from '@angular/material/snack-bar';
import { ApiService } from './api.service';
import { LoginUser } from '../modules/core/entities/login-user';

/**
 * Authentication Service
 */
@Injectable({
  providedIn: 'root',
})
export class AuthenticationService implements CanActivate {
  /**
   * User logged
   */
  currentUser: LoginUser;

  /**
   * Constructor
   *
   * @param apiService Api service
   * @param router Angular router
   * @param snackBar
   */
  constructor(private apiService: ApiService, private router: Router, private snackBar: MatSnackBar) {}

  /**
   * Login
   *
   * @param username Username string
   * @param password Password string
   *
   * @returns Session data
   */
  login({ username, password }): Observable<any> {
    return this.apiService.post('auth/login', { username, password }).pipe(
      tap((data: any) => {
        this.setSession(data);
      })
    );
  }

  /**
   * Logout
   */
  logout(): void {
    localStorage.removeItem('access_token');
    this.router.navigate(['login']);
  }

  /**
   * Set session
   *
   * @param authResult Session data
   *
   * @private
   */
  private setSession(authResult): void {
    localStorage.setItem('access_token', authResult.access_token);
    this.currentUser = LoginUser.createFromJson(authResult.user);
  }

  /**
   * Check if user is logged
   *
   * @returns Logged user
   */
  isUserLogged(): Observable<any> {
    return this.apiService.get('profile').pipe(
      tap((data: any) => {
        this.setSession(data);
      })
    );
  }

  /**
   * Check if can activate module
   *
   * @param module Module name
   *
   * @returns Boolean for module permission or navigate to login
   */
  canActivateModule(module: string): boolean | UrlTree {
    if (this.currentUser == null) {
      const message = `You aren't log in`;
      this.snackBar.open(message, 'Okey', {
        duration: 15000,
        horizontalPosition: 'center',
        verticalPosition: 'bottom',
      });
      return this.router.parseUrl('/login');
    }

    const allowAccess = this.isModuleAvailable(module);

    if (!allowAccess) {
      const message = `You don't have access to this module`;
      this.snackBar.open(message, 'Okey', {
        duration: 15000,
        horizontalPosition: 'center',
        verticalPosition: 'bottom',
      });
    }

    return allowAccess;
  }

  isModuleAvailable(module: string, modeAdmin = false, laneId: string = null): boolean {
    const isMasterUser = this.currentUser.name.toUpperCase() === 'MASTERASP';
    return (
      isMasterUser ||
      (this.currentUser.rights &&
        this.currentUser.rights.some((permission) => {
          const isPermissionEnabled = permission.enabled;
          const isMasterAdmin = permission.module === 'MASTERASP' && permission.right === 'ADMIN';
          const isModuleAdministrator = permission.module === module && permission.right === 'ADMIN';
          let hasAccessToTheModule = permission.module === module && permission.right === 'ACCESS';
          if (modeAdmin) {
            hasAccessToTheModule = false;
          }
          if (laneId) {
            hasAccessToTheModule = permission.module === module && permission.right === 'CARRIL' + laneId;
          }
          return isPermissionEnabled && (isMasterAdmin || isModuleAdministrator || hasAccessToTheModule);
        }))
    );
  }
  /**
   * Can activate
   *
   * @param route ActivatedRouteSnapshot
   * @param state RouterStateSnapshot
   *
   * @returns Boolean if can activate module
   */
  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    const module: string = route.data.module;
    if (module === 'ROOT') {
      return this.isUserLogged()
        .toPromise()
        .then((responseData) => {
          return responseData.user != null;
        })
        .catch((response: any) => {
          return response.statusCode === 200 || this.router.parseUrl('/login');
        });
    }
    return this.canActivateModule(module);
  }
}
